package com.nonopn.skywatcher.common.app.pages.map.view.views

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.width
import androidx.compose.material.Icon
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Warning
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skywatcher.common.app.home.HotPreviewApp
import com.nonopn.skywatcher.common.app.home.LocalThunderDetectionModel
import com.nonopn.skywatcher.common.app.pages.map.model.FakeMainScreenModel
import com.nonopn.skywatcher.common.app.pages.map.model.MainScreenModel
import com.nonopn.skywatcher.common.app.pages.map.model.weather.AlertType
import com.nonopn.skywatcher.common.app.pages.map.model.weather.appColor
import com.nonopn.skywatcher.common.app.pages.map.model.weather.explanation
import com.nonopn.skywatcher.common.app.pages.map.view.alerts.FormatEllapsedTimeModel
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.alert_started_at
import com.nonopn.skywatcher.common.app.res.confirm
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.LocalSubFrame
import com.nonopn.skywatcher.design.utils.WindowType
import com.nonopn.skywatcher.design.utils.localized
import de.drick.compose.hotpreview.HotPreview
import eu.codlab.compose.theme.LocalDarkTheme
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.viewmodel.rememberViewModel
import eu.codlab.weather.common.models.WeatherConditionAlertType
import korlibs.time.DateTime
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource


@Composable
fun AlertBannerAlertsTextOnly(
    modifier: Modifier,
    model: MainScreenModel,
    type: WeatherConditionAlertType,
    text: StringResource,
    drawable: DrawableResource,
    alertType: AlertType?
) {
    val state by LocalThunderDetectionModel.current.states.collectAsState()
    val modelStatus by model.states.collectAsState()
    val background = if (LocalDarkTheme.current) {
        AppColor.GraySemiTransparentLight
    } else {
        AppColor.GraySemiTransparentDark
    }

    modelStatus.probeStatus

    Box(
        modifier
    ) {
        Image(
            modifier = Modifier.fillMaxSize(),
            painter = painterResource(drawable),
            contentDescription = "",
            contentScale = ContentScale.Crop
        )

        Column(
            modifier = Modifier.fillMaxSize().background(background),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            val modifierData = Modifier.fillMaxSize()
            val isAdministrator = state.selectedThunderDetection.isAdministrator

            when (LocalSubFrame.current) {
                WindowType.SMARTPHONE_TINY -> ShowAlertBannerTinyScreen(
                    modifierData,
                    model,
                    text,
                    modelStatus.probeStatus?.alertStartedAt(),
                    alertType,
                    isAdministrator
                )

                WindowType.SMARTPHONE -> ShowAlertBannerTinyScreen(
                    modifierData,
                    model,
                    text,
                    modelStatus.probeStatus?.alertStartedAt(),
                    alertType,
                    isAdministrator
                )

                WindowType.PHABLET -> ShowAlertBannerMediumScreen(
                    modifierData,
                    model,
                    text,
                    modelStatus.probeStatus?.alertStartedAt(),
                    alertType,
                    isAdministrator
                )

                WindowType.TABLET -> ShowAlertBannerBigScreen(
                    modifierData,
                    model,
                    text,
                    modelStatus.probeStatus?.alertStartedAt(),
                    alertType,
                    isAdministrator
                )
            }
        }
    }
}

@Composable
private fun ShowAlertTypeText(
    modifier: Modifier,
    alertSentAt: DateTime?,
    text: StringResource,
    alertType: AlertType?
) {
    val infiniteTransition = rememberInfiniteTransition()
    val scaleBox by infiniteTransition.animateFloat(
        initialValue = 0.6f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(800),
            repeatMode = RepeatMode.Reverse
        )
    )

    val scaleBox2 by infiniteTransition.animateFloat(
        initialValue = 0.95f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(500),
            repeatMode = RepeatMode.Reverse
        )
    )

    Row(
        modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center
    ) {
        Icon(
            modifier = Modifier.width(60.dp).height(60.dp)
                .scale(scaleBox),
            painter = rememberVectorPainter(Icons.Default.Warning),
            contentDescription = "",
            tint = alertType.appColor()
        )

        Spacer(Modifier.width(12.dp))

        Column(
            modifier = Modifier,
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            TextEllapsedTime(Modifier.scale(scaleBox2), alertSentAt)

            Spacer(Modifier.height(12.dp))
            TextNormal(
                modifier = Modifier.scale(scaleBox2),
                text = text.localized(),
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center
            )

            Spacer(Modifier.height(12.dp))

            TextNormal(
                modifier = Modifier.scale(scaleBox2),
                text = alertType.explanation().localized(),
                fontSize = 14.sp,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center
            )
        }
    }
}

@Composable
fun TextEllapsedTime(
    modifier: Modifier,
    alertSentAt: DateTime?,
) {
    val model = rememberViewModel { FormatEllapsedTimeModel() }
    val state by model.states.collectAsState()

    LaunchedEffect(alertSentAt) {
        model.from = alertSentAt
    }

    TextNormal(
        modifier = modifier,
        text = state.ellapsedTime?.let {
            Res.string.alert_started_at.localized().replace("\$s", it)
        } ?: "",
        fontSize = 14.sp,
        fontWeight = FontWeight.Bold,
        textAlign = TextAlign.Center
    )
}

@Composable
fun ShowAlertBannerBigScreen(
    modifier: Modifier,
    model: MainScreenModel,
    text: StringResource,
    alertSentAt: DateTime?,
    alertType: AlertType?,
    isAdministrator: Boolean
) {
    Row(
        modifier,
        verticalAlignment = Alignment.CenterVertically
    ) {
        ShowAlertTypeText(
            Modifier.weight(1.0f),
            alertSentAt,
            text,
            alertType
        )

        CustomVerticalDivider()

        if (null != alertType || isAdministrator) {
            ShowAlertButton(
                modifier = Modifier.align(Alignment.CenterVertically),
                model = model,
            )
        }
    }
}

@Composable
fun ShowAlertBannerTinyScreen(
    modifier: Modifier,
    model: MainScreenModel,
    text: StringResource,
    alertSentAt: DateTime?,
    alertType: AlertType?,
    isAdministrator: Boolean
) {
    Column(
        modifier,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Column(
            Modifier.weight(2.0f),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            ShowAlertTypeText(
                Modifier.weight(1.0f),
                alertSentAt,
                text,
                alertType
            )
        }

        if (null != alertType || isAdministrator) {
            ShowAlertButton(
                modifier = Modifier.align(Alignment.CenterHorizontally),
                model = model,
            )
        }
    }
}

@Composable
fun ShowAlertBannerMediumScreen(
    modifier: Modifier,
    model: MainScreenModel,
    text: StringResource,
    alertSentAt: DateTime?,
    alertType: AlertType?,
    isAdministrator: Boolean
) {
    Row(
        modifier,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(
            Modifier.weight(2.0f),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            ShowAlertTypeText(
                Modifier.weight(1.0f),
                alertSentAt,
                text,
                alertType
            )
        }

        if (null != alertType || isAdministrator) {
            ShowAlertButton(
                modifier = Modifier.align(Alignment.CenterVertically),
                model = model,
            )
        }
    }
}

@HotPreview(widthDp = 500, heightDp = 500)
@Composable
private fun ShowAlertBannerMediumScreen() {
    HotPreviewApp(Modifier.fillMaxSize(), isPreview = true, isDarkTheme = isSystemInDarkTheme()) {
        ShowAlertBannerMediumScreen(
            Modifier.fillMaxSize(),
            FakeMainScreenModel(fakeProbeStatus = null),
            alertSentAt = null,
            text = Res.string.confirm,
            alertType = AlertType.ManualRed,
            isAdministrator = true
        )
    }
}